import { parsePhoneNumberFromString } from 'libphonenumber-js';

// Phone-entry helpers shared by the card-creation OTP fields and the
// shipping recipient fields, so paste handling and possibility checks
// behave identically everywhere a phone is typed.

export interface SplitNumber {
  readonly dialCode: string;
  readonly phoneNumber: string;
}

// Splits a pasted full international number ("+44 7947 510023" or the compact
// "+447947510023") into dial code + national digits via libphonenumber-js.
// Only attempts when an international prefix ("+" or "00") is present; a bare
// national string has no country to infer, so it stays in the field it landed
// in. Returns null when the text is not a parseable international number.
export const parseInternationalNumber = (raw: string): SplitNumber | null => {
  const trimmed = raw.trim();
  const e164 = trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
  if (!e164.startsWith('+')) return null;
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed === undefined) return null;
  return {
    dialCode: parsed.countryCallingCode.slice(0, 4),
    phoneNumber: parsed.nationalNumber.slice(0, 15),
  };
};

// True when dial code + national number form a possible number for that
// country (length-based). isPossible, not isValid: it rejects the
// wrong-length / merged-country-code mistakes that make the provider reject a
// card, without rejecting valid-length numbers the stricter metadata-based
// isValid would (fresh ranges, fictional test numbers).
export const isPossiblePhone = (dialCode: string, phoneNumber: string): boolean =>
  parsePhoneNumberFromString(`+${dialCode}${phoneNumber}`)?.isPossible() === true;

// Patch for a paste into a phone field: a full international number splits
// across both boxes; otherwise the pasted text is stripped to digits and
// capped for the field it landed in.
export const pastedPhonePatch = (
  text: string,
  primary: 'dialCode' | 'phoneNumber',
): { readonly dialCode?: string; readonly phoneNumber?: string } => {
  const split = parseInternationalNumber(text);
  if (split !== null) return split;
  const digits = text.replace(/\D/g, '');
  return primary === 'dialCode'
    ? { dialCode: digits.slice(0, 4) }
    : { phoneNumber: digits.slice(0, 15) };
};
